'use client';

import { c as _c } from "react/compiler-runtime";
import { tv } from 'tailwind-variants';
import { Checkbox, FlexContainer, FormControl, FormDescription, FormItem, FormLabel, FormMessage } from '../../components';
import { Body } from '../../typography';
import { getFieldIds } from './utils';
import type { FieldValues, Path, PathValue } from 'react-hook-form';
import type { FormOption } from '../../types';
import type { BaseComponentProps, BaseFieldProps } from './types';
const layoutVariants = tv({
  base: '',
  variants: {
    layout: {
      vertical: 'space-y-3',
      horizontal: 'flex flex-wrap gap-x-6 gap-y-3'
    }
  },
  defaultVariants: {
    layout: 'vertical'
  }
});

// Define component-specific props
export type MultiSelectCheckboxComponentProps = BaseComponentProps<'div'> & {
  options?: FormOption[]; // List of checkbox options
  layout?: 'vertical' | 'horizontal'; // Layout direction
  showSelectAll?: boolean; // Show select all option
};

// Update main props interface to extend BaseFieldProps
interface MultiSelectCheckboxFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: MultiSelectCheckboxComponentProps;
}
export function MultiSelectCheckboxField(t0) {
  const $ = _c(79);
  const {
    field,
    fieldState,
    fieldConfig,
    componentProps,
    labelSize
  } = t0;
  let className;
  let componentDisabled;
  let id;
  let restComponentProps;
  let t1;
  let t2;
  let t3;
  if ($[0] !== componentProps) {
    ({
      id,
      options: t1,
      disabled: componentDisabled,
      layout: t2,
      showSelectAll: t3,
      className,
      ...restComponentProps
    } = componentProps);
    $[0] = componentProps;
    $[1] = className;
    $[2] = componentDisabled;
    $[3] = id;
    $[4] = restComponentProps;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    className = $[1];
    componentDisabled = $[2];
    id = $[3];
    restComponentProps = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[8] = t1;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const options = t4;
  const layout = t2 === undefined ? "vertical" : t2;
  const showSelectAll = t3 === undefined ? false : t3;
  const isDisabled = field.disabled || componentDisabled;
  let t5;
  if ($[10] !== fieldConfig || $[11] !== fieldState || $[12] !== id) {
    t5 = getFieldIds(fieldConfig, fieldState, id);
    $[10] = fieldConfig;
    $[11] = fieldState;
    $[12] = id;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const {
    fieldId,
    labelId,
    descriptionId,
    errorId,
    describedBy
  } = t5;
  let t6;
  if ($[14] !== field.value) {
    t6 = Array.isArray(field.value) ? field.value as string[] : [];
    $[14] = field.value;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const selectedValues = t6;
  let t7;
  if ($[16] !== options || $[17] !== selectedValues) {
    t7 = options.length > 0 && options.every(option => selectedValues.includes(option.value));
    $[16] = options;
    $[17] = selectedValues;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const allSelected = t7;
  const someSelected = selectedValues.length > 0 && selectedValues.length < options.length;
  let t8;
  if ($[19] !== field || $[20] !== selectedValues) {
    t8 = (optionValue, checked) => {
      let newValues;
      if (checked) {
        newValues = selectedValues.includes(optionValue) ? selectedValues : [...selectedValues, optionValue];
      } else {
        newValues = selectedValues.filter(value => value !== optionValue);
      }
      field.onChange(newValues as PathValue<TFieldValues, Path<TFieldValues>>);
    };
    $[19] = field;
    $[20] = selectedValues;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleOptionChange = t8;
  let t9;
  if ($[22] !== field || $[23] !== options) {
    t9 = checked_0 => {
      if (checked_0) {
        const allValues = options.map(_temp);
        field.onChange(allValues as PathValue<TFieldValues, Path<TFieldValues>>);
      } else {
        field.onChange([] as PathValue<TFieldValues, Path<TFieldValues>>);
      }
    };
    $[22] = field;
    $[23] = options;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleSelectAllChange = t9;
  let t10;
  if ($[25] !== layout) {
    t10 = layoutVariants({
      layout
    });
    $[25] = layout;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const layoutClasses = t10;
  let t11;
  if ($[27] !== fieldConfig.label || $[28] !== fieldConfig.required || $[29] !== fieldId || $[30] !== labelId || $[31] !== labelSize) {
    t11 = <FormLabel htmlFor={fieldId} id={labelId} required={fieldConfig.required} size={labelSize} weight="medium">{fieldConfig.label}</FormLabel>;
    $[27] = fieldConfig.label;
    $[28] = fieldConfig.required;
    $[29] = fieldId;
    $[30] = labelId;
    $[31] = labelSize;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== descriptionId || $[34] !== fieldConfig.description) {
    t12 = fieldConfig.description && <FormDescription id={descriptionId}>{fieldConfig.description}</FormDescription>;
    $[33] = descriptionId;
    $[34] = fieldConfig.description;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t11 || $[37] !== t12) {
    t13 = <FlexContainer className="gap-1" direction="column">{t11}{t12}</FlexContainer>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const t14 = `${className || ""}`;
  let t15;
  if ($[39] !== allSelected || $[40] !== fieldId || $[41] !== handleSelectAllChange || $[42] !== isDisabled || $[43] !== options.length || $[44] !== showSelectAll || $[45] !== someSelected) {
    t15 = showSelectAll && options.length > 0 && <FlexContainer className="mb-3 gap-2 border-b border-border-secondary pb-3" direction="row" items="center"><Checkbox aria-label="Select all" checked={allSelected} disabled={isDisabled} id={`${fieldId}-select-all`} indeterminate={someSelected} onCheckedChange={handleSelectAllChange} /><Body className="cursor-pointer select-none" onClick={() => !isDisabled && handleSelectAllChange(!allSelected)} size="md" weight="medium">Select all</Body></FlexContainer>;
    $[39] = allSelected;
    $[40] = fieldId;
    $[41] = handleSelectAllChange;
    $[42] = isDisabled;
    $[43] = options.length;
    $[44] = showSelectAll;
    $[45] = someSelected;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== fieldId || $[48] !== handleOptionChange || $[49] !== isDisabled || $[50] !== options || $[51] !== selectedValues) {
    let t17;
    if ($[53] !== fieldId || $[54] !== handleOptionChange || $[55] !== isDisabled || $[56] !== selectedValues) {
      t17 = option_1 => {
        const optionId = `${fieldId}-option-${option_1.value}`;
        const isOptionDisabled = isDisabled || option_1.disabled;
        const isChecked = selectedValues.includes(option_1.value);
        return <FlexContainer className="gap-2" direction="row" key={option_1.value}><Checkbox className="mt-1" aria-describedby={option_1.subtext || option_1.helperText ? `${optionId}-subtext` : undefined} aria-label={option_1.label} checked={isChecked} disabled={isOptionDisabled} id={optionId} onCheckedChange={checked_1 => handleOptionChange(option_1.value, Boolean(checked_1))} /><FlexContainer direction="column"><Body className={`cursor-pointer select-none ${isOptionDisabled ? "text-content-disabled" : ""}`} onClick={() => !isOptionDisabled && handleOptionChange(option_1.value, !isChecked)} size="md" weight="medium">{option_1.label}</Body>{(option_1.subtext || option_1.helperText) && <Body className={`${isOptionDisabled ? "text-content-disabled" : "text-content-secondary"}`} id={`${optionId}-subtext`} size="md">{option_1.subtext || option_1.helperText}</Body>}</FlexContainer></FlexContainer>;
      };
      $[53] = fieldId;
      $[54] = handleOptionChange;
      $[55] = isDisabled;
      $[56] = selectedValues;
      $[57] = t17;
    } else {
      t17 = $[57];
    }
    t16 = options.map(t17);
    $[47] = fieldId;
    $[48] = handleOptionChange;
    $[49] = isDisabled;
    $[50] = options;
    $[51] = selectedValues;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[58] !== layoutClasses || $[59] !== t16) {
    t17 = <div className={layoutClasses}>{t16}</div>;
    $[58] = layoutClasses;
    $[59] = t16;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  let t18;
  if ($[61] !== options.length) {
    t18 = options.length === 0 && <Body className="text-content-secondary italic" size="sm">No options available</Body>;
    $[61] = options.length;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  let t19;
  if ($[63] !== describedBy || $[64] !== fieldId || $[65] !== labelId || $[66] !== restComponentProps || $[67] !== t14 || $[68] !== t15 || $[69] !== t17 || $[70] !== t18) {
    t19 = <FormControl><div {...restComponentProps} className={t14} aria-describedby={describedBy} aria-labelledby={labelId} id={fieldId} role="group">{t15}{t17}{t18}</div></FormControl>;
    $[63] = describedBy;
    $[64] = fieldId;
    $[65] = labelId;
    $[66] = restComponentProps;
    $[67] = t14;
    $[68] = t15;
    $[69] = t17;
    $[70] = t18;
    $[71] = t19;
  } else {
    t19 = $[71];
  }
  let t20;
  if ($[72] !== errorId || $[73] !== fieldState.error) {
    t20 = fieldState.error?.message && <FormMessage id={errorId}>{fieldState.error.message}</FormMessage>;
    $[72] = errorId;
    $[73] = fieldState.error;
    $[74] = t20;
  } else {
    t20 = $[74];
  }
  let t21;
  if ($[75] !== t13 || $[76] !== t19 || $[77] !== t20) {
    t21 = <FormItem className="flex-1">{t13}{t19}{t20}</FormItem>;
    $[75] = t13;
    $[76] = t19;
    $[77] = t20;
    $[78] = t21;
  } else {
    t21 = $[78];
  }
  return t21;
}
function _temp(option_0) {
  return option_0.value;
}